/**
 * Parcours « Bien démarrer » : modèle pur, sans React ni Firebase.
 *
 * Le parcours est une liste d'étapes rangées par phase. Chaque étape déclare
 * les capacités produit qui la rendent pertinente (offre du parc) et les
 * destinations de navigation où elle se réalise (droits de la personne). Les
 * faits lus dans les données du parc disent si elle est faite ; les
 * préférences locales de la personne disent si elle l'a écartée.
 *
 * Trois questions distinctes, jamais confondues :
 * - l'étape concerne-t-elle ce parc ?       → offre (scope produit + registre)
 * - la personne peut-elle la réaliser ?     → destinations atteignables, et le
 *                                             droit d'agir qu'elles exigent
 * - est-elle faite ?                        → faits du parc ou visite notée
 */

export const ONBOARDING_PHASES = Object.freeze(["park", "collection", "welfare", "team", "explore"]);

/** Statuts d'une étape, du plus au moins avancé. */
export const ONBOARDING_STEP_STATUSES = Object.freeze(["done", "ready", "blocked", "delegated", "skipped"]);

const DAY_MS = 24 * 60 * 60 * 1000;
/** Au-delà, une évaluation rapide mérite d'être refaite (réévaluation trimestrielle). */
export const REEVALUATION_DELAY_DAYS = 90;

const freezeList = (values = []) => Object.freeze([...values]);
const destination = (id, extra = {}) => Object.freeze({ id, ...extra });

const step = ({
  id,
  phase,
  optional = false,
  features = [],
  allFeatures = [],
  destinations,
  dependsOn = [],
  fact = null,
  visit = false,
  tourId = null,
  helpArticle = null,
}) => Object.freeze({
  id,
  phase,
  optional,
  features: freezeList(features),
  allFeatures: freezeList(allFeatures),
  destinations: freezeList(destinations),
  dependsOn: freezeList(dependsOn),
  fact,
  visit,
  tourId,
  helpArticle,
});

/**
 * Catalogue ordonné. Les destinations sont des identifiants du registre de
 * navigation V2 ; la première atteignable par la personne est retenue, ce qui
 * permet par exemple de créer les groupes depuis l'évaluation rapide quand la
 * gestion des groupes n'est pas au menu du parc.
 *
 * Ouvrir une page ne suffit pas toujours à y faire l'étape : une destination
 * peut exiger un droit d'agir (`permission`, lu par `hasPermission`).
 */
export const ONBOARDING_STEPS = Object.freeze([
  step({
    id: "sectors",
    phase: "park",
    features: ["workspace.sectors"],
    // Les secteurs sont au menu de chaque membre ; seuls l'équipe AKONGO et les
    // admin / superAdmin du parc en créent (règle Firestore `sectors`).
    destinations: [destination("collection.sectors", { permission: "sectors.administer" })],
    fact: "sectors",
    tourId: "sectorsCreate",
    helpArticle: "organisation-parc",
  }),
  step({
    id: "enclosures",
    phase: "park",
    features: ["workspace.enclosures"],
    destinations: [destination("collection.enclosures")],
    dependsOn: ["sectors"],
    fact: "enclosures",
    tourId: "enclosuresCreate",
    helpArticle: "gestion-enclos",
  }),
  step({
    id: "zims",
    phase: "park",
    optional: true,
    features: ["zims.import", "zims.syncIn"],
    destinations: [destination("account.establishment", { query: { section: "zims" }, zooScoped: true })],
    fact: "zimsConnected",
    tourId: "zimsConnect",
    helpArticle: "synchro-zims",
  }),
  step({
    id: "individuals",
    phase: "collection",
    features: ["workspace.animals"],
    destinations: [destination("collection.individuals")],
    // La saisie d'un individu comme l'import ZIMS le placent dans un enclos.
    dependsOn: ["enclosures"],
    fact: "individuals",
    tourId: "individualsCreate",
    helpArticle: "ajouter-animal",
  }),
  step({
    id: "groups",
    phase: "collection",
    features: ["workspace.groups"],
    destinations: [
      destination("collection.groups", { tourId: "groupsCreate" }),
      destination("welfare.fast-evaluation", { tourId: "fastEvaluationGroupCreate" }),
    ],
    fact: "groups",
    helpArticle: "groupes-evaluation",
  }),
  step({
    id: "groupMembers",
    phase: "collection",
    allFeatures: ["workspace.groups", "workspace.animals"],
    destinations: [destination("collection.groups")],
    dependsOn: ["individuals", "groups"],
    fact: "groupsWithIndividuals",
    tourId: "groupMembersAssign",
    helpArticle: "groupes-evaluation",
  }),
  step({
    id: "fastEvaluation",
    phase: "welfare",
    features: ["assessments.rapid"],
    destinations: [destination("welfare.fast-evaluation")],
    dependsOn: ["groups"],
    fact: "fastEvaluations",
    tourId: "fastEvaluationStart",
    helpArticle: "evaluer-animaux",
  }),
  step({
    id: "results",
    phase: "welfare",
    features: ["assessments.rapid"],
    destinations: [
      destination("welfare.fast-evaluation-results", { requires: "lastEvaluatedGroupId" }),
      destination("welfare.fast-evaluation"),
    ],
    dependsOn: ["fastEvaluation"],
    visit: true,
    tourId: "fastEvaluationResults",
    helpArticle: "evaluer-animaux",
  }),
  step({
    id: "objectives",
    phase: "welfare",
    optional: true,
    features: ["welfare.goals"],
    destinations: [destination("operations.objectives")],
    dependsOn: ["fastEvaluation"],
    // Les objectifs vivent sous chaque groupe : pas de comptage bon marché à
    // l'échelle du parc. L'étape est faite dès que la personne les a ouverts.
    fact: "objectives",
    visit: true,
    tourId: "objectivesCreate",
  }),
  step({
    id: "team",
    phase: "team",
    features: ["workspace.users"],
    destinations: [destination("account.users")],
    fact: "teammates",
    tourId: "usersInvite",
    helpArticle: "gestion-utilisateurs",
  }),
  step({
    id: "campaigns",
    phase: "explore",
    features: ["observations.campaigns"],
    destinations: [destination("welfare.campaigns")],
    fact: "campaigns",
    tourId: "campaignsCreate",
    helpArticle: "creer-campagne",
  }),
  step({
    id: "dailyReports",
    phase: "explore",
    optional: true,
    features: ["logbook.templates"],
    destinations: [destination("operations.daily-reports", { query: { section: "templates" } })],
    fact: "reportTemplates",
    tourId: "dailyReportsTemplate",
    helpArticle: "configurer-rapport",
  }),
  step({
    id: "tasks",
    phase: "explore",
    optional: true,
    features: ["logbook.tasks"],
    destinations: [destination("operations.tasks")],
    fact: "tasks",
    tourId: "tasksCreate",
    helpArticle: "gestion-taches",
  }),
  step({
    id: "enrichments",
    phase: "explore",
    optional: true,
    features: ["monitoring.enrichment"],
    destinations: [destination("enrichment.list")],
    fact: "enrichments",
    tourId: "enrichmentsCreate",
    helpArticle: "catalogue-enrichissements",
  }),
]);

export const ONBOARDING_STEP_BY_ID = Object.freeze(Object.fromEntries(
  ONBOARDING_STEPS.map((definition) => [definition.id, definition])
));

const count = (value) => (Number.isFinite(value) && value >= 0 ? value : null);
const record = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

/** Faits bruts du parc → valeurs lisibles par le catalogue. `null` = inconnu. */
export const normalizeOnboardingFacts = (facts = {}) => {
  const source = record(facts);
  const zims = record(source.zims);
  return Object.freeze({
    sectors: count(source.sectors),
    enclosures: count(source.enclosures),
    individuals: count(source.individuals),
    groups: count(source.groups),
    groupsWithIndividuals: count(source.groupsWithIndividuals),
    fastEvaluations: count(source.fastEvaluations),
    lastFastEvaluationAt: Number.isFinite(source.lastFastEvaluationAt) ? source.lastFastEvaluationAt : null,
    lastEvaluatedGroupId: typeof source.lastEvaluatedGroupId === "string" && source.lastEvaluatedGroupId
      ? source.lastEvaluatedGroupId : null,
    objectives: count(source.objectives),
    teammates: count(source.teammates),
    campaigns: count(source.campaigns),
    reportTemplates: count(source.reportTemplates),
    tasks: count(source.tasks),
    enrichments: count(source.enrichments),
    zimsConnected: typeof zims.connected === "boolean" ? zims.connected : null,
    zimsCollectionSync: zims.collectionSync === true,
  });
};

/** Préférences locales (par personne et par parc) : étapes écartées, visites notées. */
export const normalizeOnboardingPreferences = (preferences = {}) => {
  const source = record(preferences);
  const flags = (value) => Object.freeze(Object.fromEntries(Object.entries(record(value))
    .filter(([id, flag]) => ONBOARDING_STEP_BY_ID[id] && (flag === true || Number.isFinite(flag)))
    .map(([id, flag]) => [id, flag])));
  const summary = record(source.summary);
  const validSummary = Number.isInteger(summary.done) && Number.isInteger(summary.total)
    && summary.done >= 0 && summary.total >= summary.done;
  return Object.freeze({
    skipped: flags(source.skipped),
    visited: flags(source.visited),
    dismissed: source.dismissed === true,
    summary: validSummary ? Object.freeze({ done: summary.done, total: summary.total }) : null,
  });
};

const factValue = (facts, name) => {
  if (!name) return null;
  const value = facts[name];
  return typeof value === "boolean" ? (value ? 1 : 0) : value;
};

/** Faits à lire pour les étapes pertinentes : aucune lecture inutile. */
export function requiredOnboardingFacts({ scope, canParkReach }) {
  const facts = ONBOARDING_STEPS
    .filter((definition) => isInOffer(definition, scope) && definition.destinations.some(({ id }) => canParkReach(id)))
    .flatMap((definition) => [definition.fact, ...definition.destinations.map(({ requires }) => requires)])
    .filter(Boolean);
  return freezeList([...new Set(facts)]);
}

function isInOffer(definition, scope) {
  const anyOf = definition.features;
  const allOf = definition.allFeatures;
  const anyAllowed = !anyOf.length || anyOf.some((id) => scope.can(id));
  return anyAllowed && allOf.every((id) => scope.can(id));
}

/**
 * @param {object}   input
 * @param {object}   input.scope        scope produit du parc (`can(featureId)`)
 * @param {Function} input.canReach     (destinationId) => la personne peut-elle l'ouvrir ?
 * @param {Function} [input.canParkReach] (destinationId) => un administrateur du parc le pourrait-il ?
 * @param {Function} [input.hasPermission] (permissionId) => la personne détient-elle ce droit d'agir ?
 * @param {object}   [input.facts]      faits du parc (voir normalizeOnboardingFacts)
 * @param {object}   [input.preferences] préférences locales
 * @param {number}   [input.now]
 */
export function buildOnboardingJourney({
  scope,
  canReach,
  canParkReach = canReach,
  hasPermission = () => true,
  facts: rawFacts,
  preferences: rawPreferences,
  now = Date.now(),
}) {
  const facts = normalizeOnboardingFacts(rawFacts);
  const preferences = normalizeOnboardingPreferences(rawPreferences);
  const reachable = (candidate) => canReach(candidate.id)
    && (!candidate.permission || hasPermission(candidate.permission))
    && (!candidate.requires || facts[candidate.requires] !== null);
  const parkReachable = (candidate) => canParkReach(candidate.id);

  // 1. Pertinence : l'offre du parc et au moins une destination qu'un
  //    administrateur du parc pourrait ouvrir (couvre les parcs sans
  //    composition, dont l'ancien niveau de service filtre le menu).
  const applicable = ONBOARDING_STEPS.filter((definition) => isInOffer(definition, scope)
    && definition.destinations.some(parkReachable));
  const applicableIds = new Set(applicable.map(({ id }) => id));

  // 2. Accomplissement.
  const doneById = new Map(applicable.map((definition) => {
    const value = factValue(facts, definition.fact);
    const done = (value !== null && value > 0)
      || (definition.visit && Boolean(preferences.visited[definition.id]));
    return [definition.id, done];
  }));

  // 3. Statut pour la personne.
  const steps = applicable.map((definition) => {
    const done = doneById.get(definition.id);
    const target = definition.destinations.find(reachable) || null;
    const blockedBy = definition.dependsOn
      .filter((id) => applicableIds.has(id) && !doneById.get(id) && !preferences.skipped[id]);
    const skipped = !done && Boolean(preferences.skipped[definition.id]);
    const status = done ? "done"
      : skipped ? "skipped"
        : !target ? "delegated"
          : blockedBy.length ? "blocked"
            : "ready";
    return Object.freeze({
      id: definition.id,
      phase: definition.phase,
      optional: definition.optional,
      status,
      count: factValue(facts, definition.fact),
      known: definition.visit || factValue(facts, definition.fact) !== null,
      blockedBy: freezeList(blockedBy),
      destination: target
        ? Object.freeze({
          id: target.id,
          query: Object.freeze({
            ...(target.query || {}),
            ...(target.requires ? { groupId: facts[target.requires] } : {}),
          }),
          zooScoped: target.zooScoped === true,
        })
        : null,
      tourId: target ? target.tourId || definition.tourId : null,
      helpArticle: definition.helpArticle,
      // Sans registre d'individus, un groupe est une population : la vue le dit.
      variant: definition.id === "groups" && !applicableIds.has("individuals") ? "population" : null,
    });
  });

  // 4. Progression : les étapes essentielles non écartées. Une étape confiée
  //    à l'administration compte (c'est l'avancement du parc), une étape
  //    facultative ne retarde jamais la fin du parcours.
  const counted = steps.filter((item) => !item.optional && item.status !== "skipped");
  const completed = counted.filter((item) => item.status === "done").length;
  const progress = Object.freeze({
    done: completed,
    total: counted.length,
    ratio: counted.length ? completed / counted.length : 1,
  });

  // 5. Prochaine étape : la première réalisable, les essentielles d'abord.
  const nextStep = steps.find((item) => item.status === "ready" && !item.optional)
    || steps.find((item) => item.status === "ready")
    || null;

  const phases = ONBOARDING_PHASES
    .map((id) => {
      const phaseSteps = steps.filter((item) => item.phase === id);
      const essentials = phaseSteps.filter((item) => !item.optional && item.status !== "skipped");
      const phaseDone = essentials.filter((item) => item.status === "done").length;
      return Object.freeze({
        id,
        steps: freezeList(phaseSteps),
        done: phaseDone,
        total: essentials.length,
        status: essentials.length && phaseDone === essentials.length ? "done"
          : nextStep && nextStep.phase === id ? "active"
            : phaseSteps.some((item) => item.status === "done") ? "started"
              : "upcoming",
      });
    })
    .filter((phase) => phase.steps.length > 0);

  return Object.freeze({
    phases: freezeList(phases),
    steps: freezeList(steps),
    progress,
    nextStep,
    complete: counted.length > 0 && completed === counted.length,
    dismissed: preferences.dismissed,
    recommendations: buildOnboardingRecommendations({ steps, facts, canReach, now }),
  });
}

const stepById = (steps, id) => steps.find((item) => item.id === id) || null;

/**
 * Conseils contextuels, au plus trois, du plus actionnable au plus général.
 * Ils ne répètent jamais la prochaine étape : ils éclairent un choix.
 */
export function buildOnboardingRecommendations({ steps, facts, canReach, now = Date.now() }) {
  const recommendations = [];
  const add = (id, extra = {}) => recommendations.push(Object.freeze({ id, ...extra }));
  const individuals = stepById(steps, "individuals");
  const groups = stepById(steps, "groups");
  const members = stepById(steps, "groupMembers");
  const evaluation = stepById(steps, "fastEvaluation");
  const team = stepById(steps, "team");

  // L'import ZIMS place chaque animal dans un enclos : rien à proposer tant
  // que l'étape des individus attend les enclos.
  if (individuals && !["done", "blocked"].includes(individuals.status)
    && facts.zimsConnected && canReach("integration.zims")) {
    // Import du fichier ZIMS depuis la Collection ZIMS (onglet « À importer », fenêtre du fichier).
    add("zimsImport", { destination: Object.freeze({ id: "integration.zims", query: Object.freeze({ tab: "import", overlay: "file" }) }) });
  }
  if (members && members.status !== "done" && groups?.status === "done" && individuals?.status === "done") {
    add("groupsWithoutIndividuals", { destination: members.destination });
  }
  // Sans registre d'individus (offre ou ancien niveau de service), un groupe
  // représente une population : le dire évite de chercher où saisir chaque animal.
  if (groups && !individuals) {
    add("populationGroups");
  }
  if (evaluation?.status === "done" && facts.lastFastEvaluationAt
    && now - facts.lastFastEvaluationAt > REEVALUATION_DELAY_DAYS * DAY_MS) {
    add("reevaluate", {
      days: Math.floor((now - facts.lastFastEvaluationAt) / DAY_MS),
      destination: evaluation.destination,
    });
  }
  if (evaluation && evaluation.status !== "done") {
    add("mobileApp");
  }
  if (team && team.status === "ready") {
    add("inviteEarly", { destination: team.destination });
  }
  return freezeList(recommendations.slice(0, 3));
}
